/*
* User inventory template - template inventory items view for authenticated users
*/

import { filterBy, orderBy, process } from '@progress/kendo-data-query';
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid';
import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';
import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import EditGridCommandCell from '../lookup-tables/edit-grid-command-cell';
import * as ReduxQueries from '../queries';

export interface IUserInventoryTemplateProps extends AppComp.IAppComponentProps {
    userDetail?: any;
    forceRequest?: any;
    navigateTo?: any;
    userObjId?: any;
    userInventoryTemplateObjId?: any;
    userInventoryTemplate?: any;
    updateUserInventoryTemplateObj?: any;
    deleteUserInventoryTemplateItemObj?: any;
    renameUserInventoryTemplateObj?: any;
    deleteUserInventoryTemplateObj?: any;
    handleLinkNavigation?: any;
    userInvItemsWithAssocData?: any;
    userTemplateAssociation?: any;
}

export interface IUserInventoryTemplateState extends AppComp.IAppComponentState {
    formChanges?: any;
    gridVirtualSkip?: any;
    gridVirtualTake?: any;
    gridFilter?: any;
    gridSort?: any;
    editData?: any;
    showModalRemoveTemplateItem?: boolean;
    showModalRenameTemplate?: boolean;
    showModalDeleteTemplate?: boolean;
    handleLinkNavigation?: any;
    userInvItemsWithAssocData?: any;
    userTemplateAssociation?: any;
}

export const DefaultGridPageTake: number = 50;
export const DefaultFollowUpAddInterval = 7;
export const DefaultFollowUpAddPeriod = 'days';

class UserInventoryTemplate extends AppComp.AppComponent<IUserInventoryTemplateProps, IUserInventoryTemplateState> {
    public originalFormDataCache = {};
    public originalExtendedFormDataCache: Array<any> = [];
    public readonly virtualizedDropdownPageSize: number = 20;
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public anyOptionText: string = this.appTextFilterOptionAny();
    public DefaultSubgroupFilter = '#DEFAULT#';
    public readonly GridEditField: string = 'editField';
    public readonly GridNewRecordField: string = 'newRecordField';
    public readonly GridHideRecordField: string = 'hideRecordField';

    public constructor(props: IUserInventoryTemplateProps) {
        super(props, 'app.user-inventory-templates.');

        const selectedFiltersSubgroup = Formatters.getStr(props, 'filterBySubgroup.subgroup', this.DefaultSubgroupFilter);

        this.state = {
            formChanges: {},
            gridVirtualSkip: 0,
            gridVirtualTake: DefaultGridPageTake,
            gridFilter: undefined,
            gridSort: undefined,
            editData: {},
            showModalRemoveTemplateItem: false,
        };
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();
        const shouldReset = (resetFormData && _.size(this.state.formChanges)) ? true : false;

        if (shouldReset) {
            this.setState({ formChanges: {} });
        }
    }

    // Handlers
    public handleLinkNavigation(assoc: any, invId: any) {
        this.props.navigateTo(NavigationLinks.associationInventoryDetail(assoc, invId));
    }

    public handleViewUserProfileButton(e: any, userObjId: string) {
        this.props.navigateTo(NavigationLinks.userDetailObjId(userObjId));
    }

    public handleShowModalDeleteTemplate(e: any, templateId: string) {
        this.setState({ showModalDeleteTemplate: true, editData: _.cloneDeep(templateId) });
    }

    public handleShowModalRenameTemplate(e: any, templateId: string) {
        this.setState({ showModalRenameTemplate: true, editData: _.cloneDeep(templateId) });
    }

    public handleShowModalRemoveTemplateItem(e: any, selectedDataItem: any) {
        this.setState({ showModalRemoveTemplateItem: true, editData: _.cloneDeep(selectedDataItem) });
    }

    public handleCloseModalRemoveTemplateItem() {
        this.setState({ showModalRemoveTemplateItem: false, editData: {} });
    }

    public handleConfirmRemoveTemplateItem(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const curEdit = _.get(this.state, 'editData', {});
        const userInventoryTemplateObjId = Formatters.getStr(this.props, 'userInventoryTemplateObjId');
        const userInventoryTemplateInventoryItemObjId = Formatters.getStr(curEdit, '_id');

        const changeData = _.merge(
            {},
            { obj: _.pick(curEdit, ['_id']) },
            { userInventoryTemplateObjId: userInventoryTemplateObjId },
            { userInventoryTemplateInventoryItemObjId: userInventoryTemplateInventoryItemObjId },
        );

        this.props.deleteUserInventoryTemplateItemObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    showModalRemoveTemplateItem: false,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCloseModalRenameTemplate() {
        this.setState({ showModalRenameTemplate: false, editData: {} });
    }

    public handleConfirmRenameTemplate(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const curEdit = _.get(this.state, 'editData', {});
        const userInventoryTemplateObjId = Formatters.getStr(this.props, 'userInventoryTemplateObjId');
        // const userInventoryTemplateInventoryItemObjId = Formatters.getStr(curEdit, '_id');
        const requestedNewName = Formatters.getStr(curEdit, 'newName');
        const requestedNewDescription = Formatters.getStr(curEdit, 'newDescription');

        const changeData = _.merge(
            {},
            { userInventoryTemplateObjId: userInventoryTemplateObjId },
            { requestedNewName: requestedNewName, requestedNewDescription: requestedNewDescription },
        );

        this.props.renameUserInventoryTemplateObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    showModalRenameTemplate: false,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCloseModalDeleteTemplate() {
        this.setState({ showModalDeleteTemplate: false, editData: {} });
    }

    public handleConfirmDeleteTemplate(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const userInventoryTemplateObjId = Formatters.getStr(this.props, 'userInventoryTemplateObjId');
        const userObjId = Formatters.getStr(this.props, 'userDetail._id');

        const changeData = _.merge(
            {},
            { userInventoryTemplateObjId: userInventoryTemplateObjId },
        );

        this.props.deleteUserInventoryTemplateObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.props.navigateTo(NavigationLinks.userDetailObjId(userObjId));
                /* this.setState({
                    editData: {},
                    showModalDeleteTemplate: false,
                });
                this.props.forceRequest(); */
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        let fieldName = '';
        let newValue;

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', ''); // reportNotesDelete
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing fieldName ${fieldName} newValue ${newValue}`);

        if (!fieldName) { return; }

        let formChanges = _.cloneDeep(this.state.formChanges);

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        if (_.isArray(origVal)) {
            if (_.includes(origVal, newValue)) {
                // console.log(`original array already contained val ${newValue} origVal`, origVal);
                applyChange = false;
            } else {
                const formChangesArr = _.get(formChanges, fieldName, []);

                if (_.includes(formChangesArr, newValue)) {
                    // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                    applyChange = false;
                } else {
                    formChangesArr.push(newValue);
                    _.set(formChanges, fieldName, _.cloneDeep(formChangesArr));
                    applyChange = true;
                    // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChanges, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChanges, fieldName)) {
                    _.unset(formChanges, fieldName);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            this.setState({
                formChanges: formChanges
            });
        }

        console.log(`handleFormInputChange: fieldName ${fieldName} newValue ${newValue} applyChange ${applyChange}`, {
            e: e,
            formChanges: formChanges
        });
    }

    public handleGridPageChange(event: any) {
        this.setState({
            gridVirtualSkip: event.page.skip,
            gridVirtualTake: event.page.take,
        });
    }

    public handleGridFilterChange(event: any) {
        this.setState({
            gridFilter: event.filter,
        });
    }

    // BUTTON HANDLERS
    public handleEnterEdit(d: any) {
        this.handleLinkNavigation(d.associationId, d.inventoryId);
    }

    public handleEnterDetails(d: any) {
        // console.log('handleEnterDetails', { d });
        // this.props.navigateTo(NavigationLinks.lookupTableEditChildObj(tableName, childObjId));
    }

    public handleSave(d: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const userInventoryTemplateObjId = Formatters.getStr(this.props, 'userInventoryTemplateObjId');

        const changeData = _.merge(
            {},
            { obj: _.pick(d, ['_id', 'componentName', 'quantity', 'usefulLife', 'remainingUsefulLife']) },
            { userInventoryTemplateObjId: userInventoryTemplateObjId },
        );

        this.props.updateUserInventoryTemplateObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCreate(d: any) {
        // console.log(`handleCreate`, { d });
    }

    public handleCancel(d: any) {
        this.setState({
            editData: {},
        });
    }

    public handleGridItemChange(e: any) {
        let prev = _.cloneDeep(_.get(this.state, 'editData', {}));

        let n = Formatters.getStr(e, 'field');
        let v = _.get(e, 'value');

        // console.log(`handleGridItemChange n ${n} v ${v}`, { e, prev });

        if (!n) { return; }

        _.set(prev, n, v);

        this.setState({ editData: prev });
    }

    public handleShowDetails(d: any) {
        // console.log(`handleShowDetails`, { d });
        return false;
    }

    public handleShowDelete(d: any) {
        // console.log(`handleShowDelete`, { d });
        return true;
    }

    // Helpers
    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;

        // Props values
        const pa = Formatters.getInt(this.props, 'userInventoryTemplateObjId');

        // Cached values
        const ca = Formatters.getInt(this.originalFormDataCache, 'userInventoryTemplateObjId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    // Renderers
    public extendGridData(invRecords: Array<any>, invSubgroups: Array<any>, curEditData: any, curEditId: any) {
        // Extend grid row data
        if (!invRecords) { return []; }

        const extended = invRecords.map(r => {
            let record = _.cloneDeep(r);

            record.componentCategoryName = Formatters.getStr(record, 'component.category');

            const compCustomName = Formatters.getStr(record, 'componentName');
            const compNormalName = Formatters.getStr(record, 'component.name');
            record.componentName = (compCustomName) ? compCustomName : compNormalName;

            const compSubgroup = _.find(invSubgroups, ['groupCode', Formatters.getStr(record, 'subgroup')]);
            record.componentSubgroupName = Formatters.getStr(compSubgroup, 'title');

            let isEditing = (curEditId === Formatters.getStr(r, '_id')) ? true : false;

            if (isEditing) {
                const editFieldOption = _.stubObject();
                _.set(editFieldOption, this.GridEditField, isEditing);
                record = _.merge({}, r, curEditData, editFieldOption);
            }

            return record;
        });

        if (this.originalExtendedFormDataCache.length === 0) { this.originalExtendedFormDataCache = _.cloneDeep(extended); }

        return extended;
    }

    public renderHeaderButtonToolbar() {
        let infoButtonProps = {
            bsSize: 'sm',
            bsStyle: 'info',
        };

        let dangerButtonProps = {
            bsSize: 'sm',
            bsStyle: 'danger',
        };

        const { userDetail, ...rest } = this.props;
        const userObjId = Formatters.getStr(userDetail, '_id');
        const templateId = Formatters.getStr(rest, 'userInventoryTemplateObjId');

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <Button {...infoButtonProps} onClick={(e) => this.handleViewUserProfileButton(e, userObjId)}><i className="fa fa-user" /> {this.appTextCommon('buttons.view-profile')}</Button>
                                <Button {...dangerButtonProps} onClick={(e) => this.handleShowModalDeleteTemplate(e, templateId)}><i className="fa fa-trash" /> {this.appTextCommon('buttons.delete')}</Button>
                                <Button {...infoButtonProps} onClick={(e) => this.handleShowModalRenameTemplate(e, templateId)}><i className="fa fa-edit" /> {this.appTextCommon('buttons.rename')}</Button>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderModalRemoveTemplateItem() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalRemoveTemplateItem} onHide={() => this.handleCloseModalRemoveTemplateItem()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-template-item.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete-template-item.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmRemoveTemplateItem(e); }}>{this.appText('modals.delete-template-item.delete')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalRemoveTemplateItem()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalRenameTemplate() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalRenameTemplate} onHide={() => this.handleCloseModalRenameTemplate()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.rename-template.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.rename-template.body')}
                    <KendoWrappers.KendoTextInputDebounced
                        name="renameTemplate.newName"
                        label={this.appText('modals.rename-template-new-name')}
                        placeholder={this.appText('modals.rename-template-new-name.placeholder')}
                        value={Formatters.getStr(this.state.editData, 'newName')}
                        required={false}
                        handleChange={(e) => { this.setState({ editData: _.merge({}, this.state.editData, { newName: e.target.value }) }); }}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="renameTemplate.newDescription"
                        label={this.appText('modals.rename-template-new-name-description')}
                        placeholder={this.appText('modals.rename-template-new-name-description.placeholder')}
                        value={Formatters.getStr(this.state.editData, 'newDescription')}
                        required={false}
                        handleChange={(e) => { this.setState({ editData: _.merge({}, this.state.editData, { newDescription: e.target.value }) }); }}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmRenameTemplate(e); }}>{this.appText('modals.rename-template.rename')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalRenameTemplate()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalDeleteTemplate() {
        return (
            <Modal enforceFocus={false} show={this.state.showModalDeleteTemplate} onHide={() => this.handleCloseModalDeleteTemplate()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-template.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete-template.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" onClick={(e) => { this.handleConfirmDeleteTemplate(e); }}>{this.appText('modals.delete-template.delete')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalDeleteTemplate()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { userDetail, userInventoryTemplate: currentUserInvTemplate, userInvItemsWithAssocData, userTemplateAssociation, ...rest } = this.props;
        // const currentUserInvTemplate = _.get(this.props, 'userInventoryTemplate');
        // const templateInventoryItems = _.get(currentUserInvTemplate, 'templateInventoryItems', []);
        const templateSubgroups = _.get(userTemplateAssociation, 'inventorySubgroups', []);
        const invItemsFromTemplateAssoc = userInvItemsWithAssocData;

        // const rows: Array<any> = this.extendGridData(templateInventoryItems, templateSubgroups, this.state.formChanges);

        const curEditData = _.get(this.state, 'editData', {});
        const curEditId = Formatters.getStr(curEditData, '_id');

        // const rows: Array<any> = this.extendGridData(templateInventoryItems, templateSubgroups, curEditData, curEditId);
        const rows: Array<any> = this.extendGridData(invItemsFromTemplateAssoc, templateSubgroups, curEditData, curEditId);

        const pageTitle = this.appTextResultsAndPageTitle(rows.length);
        let gridTitle = this.appTextResultsAndPageTitle(rows.length);

        let page = (<div />);

        if (userDetail) {
            const rowRenderer = (trElement: React.ReactElement<any>, dataItem: any) => {
                const trProps = { ...trElement.props };

                const addClass = _.get(dataItem.dataItem, GridUtilities.KendoGridAddRowClassFieldName, '');
                if (addClass) { trProps.className += ` ${addClass}`; }

                return React.cloneElement(trElement, { ...trProps }, trElement.props.children);
            };

            const gridDataFiltered = filterBy(rows, this.state.gridFilter);
            let gridData = gridDataFiltered.slice(this.state.gridVirtualSkip, this.state.gridVirtualTake + this.state.gridVirtualSkip);

            // const formData = this.getFormData(currentUserInvTemplate);

            // if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
            //     this.originalFormDataCache = _.cloneDeep(formData);
            // }

            gridTitle = `"${Formatters.getStr(currentUserInvTemplate, 'name')}" (${_.size(rows)} ${this.appText('grid-title.template-items')})`;

            let gridProps: any = {};
            gridProps.sortable = true;
            const defaultGridSort = [{ field: 'componentSubgroupName', dir: 'asc' }, { field: 'componentId', dir: 'asc' }];
            const curStateGridSortRaw = (_.get(this.state, 'gridSort')) ? _.get(this.state, 'gridSort') : defaultGridSort;
            const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
            gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
            gridProps.sortChange = (e: any) => {
                const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                this.setState({ 'gridSort': eventGridSortNext });
            };

            const gridDataSorted = orderBy(gridData, curStateGridSort);
            const gridDataSource = process(gridDataSorted, {});

            const canSave = (d: any) => !_.isEmpty(Formatters.getStr(d, 'componentName'));

            const editGridCommandCell = EditGridCommandCell(
                (dataItem: any) => this.handleEnterEdit(dataItem),
                (e: any, dataItem: any) => this.handleShowModalRemoveTemplateItem(e, dataItem),
                (dataItem: any) => this.handleSave(dataItem),
                (dataItem: any) => this.handleCreate(dataItem),
                (dataItem: any) => this.handleCancel(dataItem),
                (dataItem: any) => undefined,
                (dataItem: any) => this.handleShowDetails(dataItem),
                (dataItem: any) => this.handleShowDelete(dataItem),
                (dataItem: any) => canSave(dataItem),
                this.GridEditField,
                this.GridNewRecordField,
                (m?: any, p?: any, t?: any) => this.appText(m, p, t));

            const cellProps: any = {};
            // cellProps.cell = CellWithTruncate;

            const grid = (
                <Grid
                    key="user-inventory-template-edit-grid"
                    data={gridDataSource}
                    resizable={true}
                    rowRender={rowRenderer}
                    filterable={true}
                    filter={_.get(this.state, 'gridFilter')}
                    filterChange={(e) => { this.handleGridFilterChange(e); }}
                    style={{ width: '100%', height: '750px' }}
                    skip={this.state.gridVirtualSkip}
                    take={this.state.gridVirtualTake}
                    total={_.size(rows)}
                    pageable={true}
                    editField={this.GridEditField}
                    pageChange={(e) => { this.handleGridPageChange(e); }}
                    itemChange={(e) => { this.handleGridItemChange(e); }}
                    {...gridProps}
                >
                    <Column field="componentSubgroupName" title={this.appTextGridColumnHeader('component-subgroup-name')} width="130px" editable={false} sortable={true} {...cellProps} /> { /* cell={CellWithTruncate} /> */}
                    <Column field="componentId" title={this.appTextGridColumnHeader('component-id')} width="120px" editable={false} filter="numeric" sortable={true} {...cellProps} /> { /*  cell={CellWithTruncate} filter="numeric" /> */}
                    <Column field="componentName" title={this.appTextGridColumnHeader('component-name')} editable={true} sortable={true} {...cellProps} /> { /*  cell={CellWithTruncate} /> */}
                    <Column field="componentCategoryName" title={this.appTextGridColumnHeader('category-name')} width="160px" editable={false} sortable={true} {...cellProps} /> { /*  cell={CellWithTruncate} /> */}
                    <Column field="quantity" title={this.appTextGridColumnHeader('quantity')} width="200px" editable={true} sortable={true} {...cellProps} /> { /*  cell={CellWithTruncate} /> */}
                    <Column field="usefulLife" title={this.appTextGridColumnHeader('useful-life')} width="60px" editable={true} sortable={true} {...cellProps} /> { /*  cell={CellWithTruncate} /> */}
                    <Column field="remainingUsefulLife" title={this.appTextGridColumnHeader('remaining-useful-life')} width="60px" editable={true} filter="numeric" sortable={true} {...cellProps} /> { /*  cell={CellWithTruncate} /> */}

                    <Column
                        field=""
                        title=" "
                        width="150px"
                        cell={editGridCommandCell}
                    />
                </Grid>
            );

            page = (
                <div>
                    {this.renderHeaderButtonToolbar()}

                    <WhiteBoxPageSection title={gridTitle} >
                        {this.renderModalRenameTemplate()}
                        {this.renderModalDeleteTemplate()}
                        {this.renderModalRemoveTemplateItem()}
                        <Row>
                            <Col xs={12}>
                                {grid}
                            </Col>
                        </Row>
                    </WhiteBoxPageSection>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = (objId) => ApiEndpoints.getUserInventoryTemplateUrl(objId);

const mapPropsToConfigs = (props) => {
    return [
        ReduxQueries.Queries.getUserInventoryTemplateRequest(
            getApiUrl(Formatters.getStr(props, 'userInventoryTemplateObjId')),
            ReduxQueries.PropsAccessToken(props),
        ),
    ];
};

const mapStateToProps = (state, props) => {
    const userInventoryTemplateObjId = Formatters.getStr(props, 'match.params.userInventoryTemplateObjId');

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(userInventoryTemplateObjId), state, props, {
        userInventoryTemplateObjId: userInventoryTemplateObjId,
        userObjId: 'testing-userobjid',
        userDetail: state.entities.userInvTemplateUser,
        userInventoryTemplate: state.entities.userInvTemplateUserInventoryTemplate,
        userInvItemsWithAssocData: state.entities.userInvItemsWithAssocData,
        userTemplateAssociation: state.entities.userTemplateAssociation,
    });
};

const mapDispatchToProps = {
    updateUserInventoryTemplateObj: MutateActions.updateUserInventoryTemplateMutateAsync,
    renameUserInventoryTemplateObj: MutateActions.renameUserInventoryTemplateMutateAsync,
    deleteUserInventoryTemplateObj: MutateActions.deleteUserInventoryTemplateMutateAsync,
    deleteUserInventoryTemplateItemObj: MutateActions.deleteUserInventoryTemplateItemMutateAsync,
    navigateTo: (path) => push(path)
};

const UserInventoryTemplateContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(UserInventoryTemplate);

export default withRouter(UserInventoryTemplateContainer);



// WEBPACK FOOTER //
// ./src/user-inventory-template/index.tsx